import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import { HeadersFunction, LoaderFunction, MetaFunction, useLoaderData, useSearchParams } from "react-router";
import { KirtanCharactersScrollBar } from "~/components/kirtan-lyrics/KirtanCharactersScrollBar";
import KirtanDetailPanel from "~/components/kirtan-lyrics/KirtanDetailPanel";
import KirtanLyricsData from "~/components/kirtan-lyrics/KirtanLyricsData";
import KirtanLyricsFilter from "~/components/kirtan-lyrics/KirtanLyricsFilter";
import { useLanguageParams } from "~/hook/useLanguageParams";
import useLocale from "~/hook/useLocale";
import {
  KirtalLyricsPadListData,
  KirtanLyricsdata,
  KirtanLyricsLoaderData,
  KirtanLyricsSyncData,
  selectedFilterDataType,
} from "~/interface/kirtan-lyrics/kirtan-lyrics";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import {
  setBhavData,
  setHoveredChar,
  setKirtanBookData,
  setKirtanCharacters,
  setKirtanLyricsData,
  setKirtanPlaceData,
  setKirtanPrasangData,
  setRaagDhaalData,
  setRachayataData,
  setSelectedBhav,
  setSelectedKBook,
  setSelectedKPlace,
  setSelectedKPrasang,
  setSelectedRaagDhal,
  setSelectedRachayta,
} from "~/lib/store/slice/kirtanLyricsSlice";
import { setSearchText } from "~/lib/store/slice/searchSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { MstPersonType } from "~/type";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import IndexedDBService, { storeNames } from "~/utils/dbConfig";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

import { Funnel, FunnelX } from "lucide-react";
import { Drawer, DrawerContent, DrawerTrigger } from "~/components/ui/drawer";
import { useIsMobile } from "~/hook/use-mobile";
import useDisplayStickyButton from "~/hook/useDisplayStickyButton";
import { useFontStyle } from "~/hook/useFontStyle";
import { computeEtagFromData, stripHTML } from "~/utils/helper-funcations";

export const meta: MetaFunction = ({ location, params, data }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";
  const loaderData = data as KirtanLyricsLoaderData;
  const selectedKirtanPad = loaderData?.kirtanPadListData?.[0] || {};
  const meta = getPageMeta("kirtanLyrics", lang);

  // If a specific kirtan is selected, use its title
  if (selectedKirtanPad.title) {
    const title = `${selectedKirtanPad.title} | Swaminarayanbhagwan.org`;
    const desc = selectedKirtanPad.text ? `${stripHTML(selectedKirtanPad.text)}` : meta.metaDescription;

    return metaService({
      title: title,
      description: desc,
      ogTitle: title,
      ogDescription: desc,
      ogUrl: pathname,
      ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
      ogWidth: meta.ogImageWidth || "1200",
      ogHeight: meta.ogImageHeight || "630",
      canonicalLink: pathname,
      hreflangs: [
        { hrefLang: "x-default", href: `${pathname}` },
        { hrefLang: "en", href: `${pathname}` },
        { hrefLang: "gu", href: `${pathname}` },
      ],
    });
  }

  // Build dynamic title based on filters
  const selectedFilters = loaderData?.selectedFilters || {};
  const kirtanLyricsData = loaderData?.kirtanLyricsData || [];
  const resultCount = kirtanLyricsData.length || 0;

  // Check which filters are applied
  const hasPrasang = selectedFilters.k_prasang?.length > 0;
  const hasBhav = selectedFilters.bhav?.length > 0;
  const hasRachayta = selectedFilters.rachayta?.length > 0;
  const hasBook = selectedFilters.k_book?.length > 0;
  const hasPlace = selectedFilters.k_place?.length > 0;
  const hasRaagDhal = selectedFilters.raag_dhal?.length > 0;

  // Check if ANY filter is applied
  const hasAnyFilter = hasPrasang || hasBhav || hasRachayta || hasBook || hasPlace || hasRaagDhal;

  // Language-specific text
  const isGujarati = lang === "gu";
  const texts = {
    swaminarayan: isGujarati ? "સ્વામિનારાયણ" : "Swaminarayan",
    kirtanLyrics: isGujarati ? "કીર્તન લિરિક્સ" : "Kirtan Lyrics",
    by: isGujarati ? "દ્વારા" : "by",
    from: isGujarati ? "માંથી" : "from",
    bhajanKirtanWords: isGujarati ? "ભજન અને કીર્તન શબ્દો" : "Bhajan & Kirtan Words",
    byNandSaints: isGujarati ? "નંદ સંત દ્વારા" : "by Nand Saints",
  };

  // Build title parts
  let titleParts = [texts.swaminarayan];

  // Add prasang (if exists)
  if (hasPrasang) {
    titleParts.push(selectedFilters.k_prasang.map((item) => item.text).join(", "));
  }

  // Add bhav with comma (only if BOTH prasang AND bhav exist)
  if (hasPrasang && hasBhav) {
    titleParts[titleParts.length - 1] = titleParts[titleParts.length - 1] + ", " + selectedFilters.bhav.map((item) => item.text).join(", ");
  } else if (hasBhav) {
    // Add bhav without comma if only bhav exists
    titleParts.push(selectedFilters.bhav.map((item) => item.text).join(", "));
  }

  // Add "Kirtan Lyrics"
  titleParts.push(texts.kirtanLyrics);

  // Add rachyta (composer) in parentheses - format: (દ્વારા નામ) or (by Name)
  if (hasRachayta) {
    const rachytaNames = selectedFilters.rachayta.map((item) => item.text).join(", ");
    titleParts.push(`${rachytaNames} ${texts.by}`);
  }

  // Add book in parentheses - format: (પુસ્તક માંથી) or (from Book)
  if (hasBook) {
    titleParts.push(`${selectedFilters.k_book.map((item) => item.text).join(", ")} ${texts.from}`);
  }

  // Format count with commas (e.g., 10,500 or ૧૦,૫૦૦)
  const formattedCount = isGujarati ? resultCount.toLocaleString("gu-IN") : resultCount.toLocaleString("en-US");

  // Add count section
  if (hasAnyFilter) {
    // With filters: show count without "by Nand Saints"
    titleParts.push(`– ${formattedCount}+ ${texts.bhajanKirtanWords}`);
  } else {
    // No filters: show count WITH "by Nand Saints"
    const defaultCount = isGujarati ? "૧૦,૦૦૦+" : "10,000+";
    titleParts.push(`– ${defaultCount} ${texts.bhajanKirtanWords} ${texts.byNandSaints}`);
  }

  // Join all parts with spaces and add domain
  const title = `${titleParts.join(" ")} | SwaminarayanBhagwan.org`;

  // Build dynamic description
  const descTexts = {
    explore: isGujarati ? "અન્વેષણ કરો" : "Explore",
    discover: isGujarati ? "શોધો" : "Discover",
    collection: isGujarati ? "સંગ્રહ" : "collection",
    of: isGujarati ? "ના" : "of",
    bhajans: isGujarati ? "ભજનો" : "bhajans",
    kirtans: isGujarati ? "કીર્તનો" : "kirtans",
    and: isGujarati ? "અને" : "and",
    with: isGujarati ? "સાથે" : "with",
    lyrics: isGujarati ? "લિરિક્સ" : "lyrics",
    words: isGujarati ? "શબ્દો" : "words",
    complete: isGujarati ? "સંપૂર્ણ" : "complete",
    written: isGujarati ? "લખાયેલ" : "written",
    composed: isGujarati ? "રચિત" : "composed",
    available: isGujarati ? "ઉપલબ્ધ" : "available",
    read: isGujarati ? "વાંચો" : "Read",
    browse: isGujarati ? "બ્રાઉઝ કરો" : "Browse",
  };

  let descParts = [];

  if (hasAnyFilter) {
    // With filters: More specific description
    if (isGujarati) {
      descParts.push(descTexts.explore);

      // Add count
      descParts.push(`${formattedCount}+`);

      // Add type based on filters
      if (hasPrasang) {
        descParts.push(selectedFilters.k_prasang.map((item) => item.text).join(", "));
      }

      if (hasBhav && !hasPrasang) {
        descParts.push(selectedFilters.bhav.map((item) => item.text).join(", "));
      }

      descParts.push(`${texts.swaminarayan} ${descTexts.bhajans} ${descTexts.and} ${descTexts.kirtans}`);

      if (hasRachayta) {
        const rachytaNames = selectedFilters.rachayta.map((item) => item.text).join(", ");
        descParts.push(`${rachytaNames} ${texts.by} ${descTexts.written}`);
      }

      if (hasBook) {
        const bookNames = selectedFilters.k_book.map((item) => item.text).join(", ");
        descParts.push(`${bookNames} ${texts.from}`);
      }

      descParts.push(`${descTexts.complete} ${descTexts.lyrics} ${descTexts.with} ${descTexts.words}.`);
    } else {
      descParts.push(descTexts.discover);
      descParts.push(`${formattedCount}+`);

      if (hasPrasang) {
        descParts.push(selectedFilters.k_prasang.map((item) => item.text).join(", "));
      }

      if (hasBhav && !hasPrasang) {
        descParts.push(selectedFilters.bhav.map((item) => item.text).join(", "));
      }

      descParts.push(`${texts.swaminarayan} ${descTexts.bhajans} ${descTexts.and} ${descTexts.kirtans}`);

      if (hasRachayta) {
        const rachytaNames = selectedFilters.rachayta.map((item) => item.text).join(", ");
        descParts.push(`${descTexts.composed} by ${rachytaNames}`);
      }

      if (hasBook) {
        const bookNames = selectedFilters.k_book.map((item) => item.text).join(", ");
        descParts.push(`from ${bookNames}`);
      }

      descParts.push(`with ${descTexts.complete} ${descTexts.lyrics} ${descTexts.and} ${descTexts.words}.`);
    }
  } else {
    // No filters: General description with "by Nand Saints"
    if (isGujarati) {
      const defaultCount = "૧૦,૦૦૦+";
      descParts.push(
        `${descTexts.browse} ${defaultCount} ${texts.swaminarayan} ${descTexts.bhajans}, ${descTexts.kirtans} ${descTexts.and} ${descTexts.lyrics} ${texts.byNandSaints} ${descTexts.written}. ${descTexts.complete} ${descTexts.collection} ${descTexts.words} ${descTexts.and} ${descTexts.lyrics} ${descTexts.with} ${descTexts.available}.`,
      );
    } else {
      descParts.push(
        `${descTexts.browse} over 10,000+ ${texts.swaminarayan} ${descTexts.bhajans}, ${descTexts.kirtans} ${descTexts.and} ${descTexts.lyrics} ${texts.byNandSaints}. ${descTexts.complete} ${descTexts.collection} ${descTexts.of} ${descTexts.words}, ${descTexts.lyrics} ${descTexts.and} devotional songs ${descTexts.available} online.`,
      );
    }
  }

  const desc = descParts.join(" ");

  return metaService({
    title: title,
    description: desc,
    ogTitle: title,
    ogDescription: desc,
    ogUrl: pathname,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: `${pathname}${location.search}`,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}${location.search}` },
      { hrefLang: "en", href: `${pathname}${location.search}` },
      { hrefLang: "gu", href: `${pathname}${location.search}` },
    ],
  });
};

export const loader: LoaderFunction = async ({ params, request }) => {
  const lang = params.lang || LANG.english;
  const searchParams = new URL(request.url).searchParams;
  const kirtanId = searchParams.get("kirtanId");
  const apiService = new ApiService(lang);

  // Don't fetch kirtanPadListData in loader - it will be fetched client-side
  // This keeps loader fast and avoids issues with client-side URL changes

  // Extract filter values and normalize them to lowercase arrays
  const selectedRaagDhal = (searchParams.get("raagdhal") ?? "")
    .split(",")
    .filter(Boolean)
    .map((s) => s?.toLowerCase());
  const selectedBhav = (searchParams.get("bhav") ?? "")
    .split(",")
    .filter(Boolean)
    .map((s) => s?.toLowerCase());
  const selectedRachayta = (searchParams.get("rachayta") ?? "")
    .split(",")
    .filter(Boolean)
    .map((s) => s?.toLowerCase());
  const selectedKBook = (searchParams.get("k_book") ?? "")
    .split(",")
    .filter(Boolean)
    .map((s) => s?.toLowerCase());
  const selectedKPlace = (searchParams.get("k_place") ?? "")
    .split(",")
    .filter(Boolean)
    .map((s) => s?.toLowerCase());
  const selectedKPrasang = (searchParams.get("k_prasang") ?? "")
    .split(",")
    .filter(Boolean)
    .map((s) => s?.toLowerCase());

  // Fetch master data and kirtan characters
  const [masterData, kirtanCharacters] = await Promise.all([apiService.getMasterData(), apiService.getKirtanCharacters()]);
  const selectedPad = kirtanId ? await apiService.getKirtanLyricsPadListOfKirtan(Number(kirtanId)) : null;

  const RaagDhaalData = masterData?.raags_and_taals || [];
  const BhavData = masterData?.bhav || [];
  const CreatorData = masterData?.rachayta || [];
  const KirtanBookData = masterData?.kirtan_book || [];
  const KirtanPlaceData = masterData?.kirtan_place || [];
  const KirtanPrasangData = masterData?.kirtan_prasang || [];
  const GayakData = masterData?.gayak || [];

  // Helper to map selected text filters to their full master data entries
  const filterSelectedItems = (selected: string[], master: MstPersonType[]): selectedFilterDataType[] => {
    return master.filter((item) => selected.includes(item.text?.toLowerCase()));
  };

  const raag_dhal = selectedRaagDhal.length ? filterSelectedItems(selectedRaagDhal, RaagDhaalData) : [];
  const bhav = selectedBhav.length ? filterSelectedItems(selectedBhav, BhavData) : [];
  const rachayta = selectedRachayta.length ? filterSelectedItems(selectedRachayta, CreatorData) : [];
  const kirtan_book = selectedKBook.length ? filterSelectedItems(selectedKBook, KirtanBookData) : [];
  const kirtan_place = selectedKPlace.length ? filterSelectedItems(selectedKPlace, KirtanPlaceData) : [];
  const kirtan_prasang = selectedKPrasang.length ? filterSelectedItems(selectedKPrasang, KirtanPrasangData) : [];

  const title = lang === LANG.gujarati ? "કીર્તન" : "Kirtan Lyrics";

  const kirtanLyricsResponse = await apiService.getKirtanLyricsData({
    raag_dhal: raag_dhal.map((item) => item.id),
    bhav: bhav.map((item) => item.id),
    rachayta: rachayta.map((item) => item.id),
    k_book: kirtan_book.map((item) => item.id),
    k_place: kirtan_place.map((item) => item.id),
    k_prasang: kirtan_prasang.map((item) => item.id),
    pageIndex: 1,
    pageSize: 1000,
  });

  const DATA = {
    title,
    kirtanCharacters,
    RaagDhaalData,
    BhavData,
    CreatorData,
    KirtanBookData,
    KirtanPlaceData,
    KirtanPrasangData,
    kirtanLyricsData: kirtanLyricsResponse?.rows || [],
    GayakData,
    selectedFilters: {
      raag_dhal,
      bhav,
      rachayta,
      k_book: kirtan_book,
      k_place: kirtan_place,
      k_prasang: kirtan_prasang,
    },
    kirtanId: kirtanId,
    kirtanPadListData: selectedPad || [],
  };

  const etag = computeEtagFromData(DATA);
  const ifNoneMatch = request.headers.get("If-None-Match");
  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: { ...CACHED_PAGE.KIRTAN_LYRICS.HEADERS, ETag: etag },
  });
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

const KirtanLyrics = () => {
  const lang = useLanguageParams();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const { navigate } = useLocale();
  const { showTopButton } = useDisplayStickyButton();
  const { normalInter } = useFontStyle();

  const {
    kirtanCharacters,
    RaagDhaalData,
    BhavData,
    CreatorData,
    selectedFilters,
    kirtanLyricsData,
    kirtanId,
    kirtanPadListData,
    KirtanBookData,
    KirtanPlaceData,
    KirtanPrasangData,
    GayakData,
  } = useLoaderData<KirtanLyricsLoaderData>();

  // const [urlKirtanId, setUrlKirtanId] = useState<string | null>(null);
  const [queryKirtanId, setQueryKirtanId] = useState<string | null>(null);
  const [searchText, setSearchText_local] = useState<string>("");

  // On mobile, read search text from the global Redux search slice
  // (user types in the header SearchBar which is suppressed from showing its modal)
  const reduxSearchText = useAppSelector((state) => state.search.searchText);
  const effectiveSearchText = (isMobile ? reduxSearchText : searchText) ?? "";

  const allSelectedFilterData = [...selectedFilters.bhav, ...selectedFilters.raag_dhal, ...selectedFilters.rachayta];

  const { isTouchBar } = useAppSelector((state) => state.kirtanLyrics);

  const apiService = useMemo(() => new ApiService(lang || LANG.english), [lang]);

  const [allData, setAllData] = useState<KirtanLyricsdata[]>(kirtanLyricsData);
  const [selectedKirtanDetail, setSelectedKirtanDetail] = useState<KirtanLyricsdata | null>(null);
  const [padDataCache, setPadDataCache] = useState<Map<number, KirtalLyricsPadListData[]>>(new Map());
  const [selectedPadList, setSelectedPadList] = useState<KirtalLyricsPadListData[] | null>(null);

  // Initialize queryKirtanId on mount and restore padDataCache from sessionStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      // Restore padDataCache from sessionStorage
      try {
        const cachedDataStr = sessionStorage.getItem("padDataCache");
        if (cachedDataStr) {
          const parsedData = JSON.parse(cachedDataStr) as Record<string, KirtalLyricsPadListData[]>;
          const mapData = new Map(Object.entries(parsedData).map(([k, v]) => [Number(k), v]));
          setPadDataCache(mapData);
        }
      } catch (error) {}

      // Get kirtanId from URL
      const url = new URL(window.location.href);
      const urlParam = url.searchParams.get("kirtanId");
      setQueryKirtanId(urlParam || null);
    }
  }, []);

  const handleCloseDetailPanel = () => {
    setSelectedKirtanDetail(null);
    // setUrlKirtanId(null);
    setQueryKirtanId(null);

    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("kirtanId");
      window.history.replaceState(null, "", url.toString());
      window.dispatchEvent(new CustomEvent("kirtanIdChanged", { detail: { kirtanId: null } }));
    }
  };

  // Initialize Redux state only once for master data
  useEffect(() => {
    dispatch(setBhavData(BhavData));
    dispatch(setRaagDhaalData(RaagDhaalData));
    dispatch(setRachayataData(CreatorData));
    dispatch(setKirtanCharacters(kirtanCharacters));
    dispatch(setKirtanBookData(KirtanBookData));
    dispatch(setKirtanPlaceData(KirtanPlaceData));
    dispatch(setKirtanPrasangData(KirtanPrasangData));
  }, [BhavData, RaagDhaalData, CreatorData, kirtanCharacters, KirtanBookData, KirtanPlaceData, KirtanPrasangData, dispatch]);

  useEffect(() => {
    dispatch(setSelectedBhav(selectedFilters.bhav));
    dispatch(setSelectedRaagDhal(selectedFilters.raag_dhal));
    dispatch(setSelectedRachayta(selectedFilters.rachayta));
    dispatch(setSelectedKBook(selectedFilters.k_book));
    dispatch(setSelectedKPlace(selectedFilters.k_place));
    dispatch(setSelectedKPrasang(selectedFilters.k_prasang));
  }, [
    selectedFilters.bhav,
    selectedFilters.raag_dhal,
    selectedFilters.rachayta,
    selectedFilters.k_book,
    selectedFilters.k_place,
    selectedFilters.k_prasang,
    dispatch,
  ]);

  const filteredData = useMemo(() => {
    let data = allData;
    if (effectiveSearchText.trim()) {
      const searchLower = effectiveSearchText.toLowerCase();
      const searchField = lang === LANG.gujarati ? "gt" : "et";
      data = data.filter((kirtan) => {
        const fieldValue = kirtan[searchField as keyof KirtanLyricsdata];
        return fieldValue && String(fieldValue).toLowerCase().includes(searchLower);
      });
    }

    if (
      selectedFilters.raag_dhal.length === 0 &&
      selectedFilters.bhav.length === 0 &&
      selectedFilters.rachayta.length === 0 &&
      selectedFilters.k_book.length === 0 &&
      selectedFilters.k_place.length === 0 &&
      selectedFilters.k_prasang.length === 0
    ) {
      return data;
    }

    const raagDhalIds = selectedFilters?.raag_dhal.map((item) => item.id);
    const bhavIds = selectedFilters.bhav.map((item) => item.id);
    const rachayataIds = selectedFilters.rachayta.map((item) => item.id);
    const kBookIds = selectedFilters.k_book.map((item) => item.id);
    const kPlaceIds = selectedFilters.k_place.map((item) => item.id);
    const kPrasangIds = selectedFilters.k_prasang.map((item) => item.id);

    return data?.filter((kirtan) => {
      const matchesRaagDhal = raagDhalIds?.length === 0 || (Array.isArray(kirtan?.rd) && kirtan.rd.some((id) => raagDhalIds?.includes(id)));
      const matchesBhav = bhavIds?.length === 0 || (Array.isArray(kirtan?.bh) && kirtan.bh.some((id) => bhavIds?.includes(id)));
      const matchesRachayta = rachayataIds?.length === 0 || (kirtan?.r && rachayataIds?.includes(kirtan.r));
      const matchesKBook = kBookIds?.length === 0 || (kirtan?.b && kBookIds?.includes(kirtan.b));
      const matchesKPlace = kPlaceIds?.length === 0 || (Array.isArray(kirtan?.p) && kirtan.p.some((id) => kPlaceIds?.includes(id)));
      const matchesKPrasang = kPrasangIds?.length === 0 || (Array.isArray(kirtan?.pr) && kirtan.pr.some((id) => kPrasangIds?.includes(id)));
      return matchesRaagDhal && matchesBhav && matchesRachayta && matchesKBook && matchesKPlace && matchesKPrasang;
    });
  }, [
    allData,
    effectiveSearchText,
    lang,
    selectedFilters.raag_dhal,
    selectedFilters.bhav,
    selectedFilters.rachayta,
    selectedFilters.k_book,
    selectedFilters.k_place,
    selectedFilters.k_prasang,
  ]);

  // Set selected kirtan detail when kirtanId changes (from URL query params only)
  useEffect(() => {
    if (queryKirtanId && allData.length > 0) {
      const found = allData.find((kirtan) => kirtan.i === Number(queryKirtanId));
      setSelectedKirtanDetail(found || null);
    } else {
      setSelectedKirtanDetail(null);
    }
  }, [queryKirtanId, allData]);

  // Listen for browser back/forward and URL changes
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handlePopState = () => {
      const url = new URL(window.location.href);
      const newKirtanId = url.searchParams.get("kirtanId");
      setQueryKirtanId(newKirtanId || null);
    };

    const handleURLChange = () => {
      const url = new URL(window.location.href);
      const newKirtanId = url.searchParams.get("kirtanId");
      setQueryKirtanId(newKirtanId || null);
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("kirtanIdChanged", handleURLChange as EventListener);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("kirtanIdChanged", handleURLChange as EventListener);
    };
  }, [lang]);

  // Watch for direct URL changes (when user types in address bar or hash changes)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkUrlChange = () => {
      const url = new URL(window.location.href);
      const newKirtanId = url.searchParams.get("kirtanId");
      setQueryKirtanId(newKirtanId || null);
    };

    // Use MutationObserver or polling to detect URL changes
    const interval = setInterval(checkUrlChange, 0);
    return () => clearInterval(interval);
  }, []);

  // Save padDataCache to sessionStorage whenever it changes
  useEffect(() => {
    if (typeof window !== "undefined" && padDataCache.size > 0) {
      const serialized = Object.fromEntries(padDataCache);
      sessionStorage.setItem("padDataCache", JSON.stringify(serialized));
    }
  }, [padDataCache]);

  // Fetch pad data when queryKirtanId changes (handles direct URL changes)
  useEffect(() => {
    if (!queryKirtanId) return;

    // Check if already cached
    if (padDataCache.has(Number(queryKirtanId))) {
      return;
    }

    // If queryKirtanId matches loader's kirtanId, use loader data directly
    if (String(queryKirtanId) === String(kirtanId)) {
      setPadDataCache((prev) => new Map(prev).set(Number(queryKirtanId), kirtanPadListData || []));
      return;
    }

    // Only fetch if it's a different kirtan not in cache
    const fetchPadData = async () => {
      try {
        const padData = await apiService.getKirtanLyricsPadListOfKirtan(Number(queryKirtanId));
        setPadDataCache((prev) => new Map(prev).set(Number(queryKirtanId), padData || []));
      } catch (error) {}
    };

    fetchPadData();
  }, [queryKirtanId, kirtanId, kirtanPadListData, apiService]);

  // Clear selectedKirtanDetail when queryKirtanId is removed
  useEffect(() => {
    if (!queryKirtanId) {
      setSelectedKirtanDetail(null);
    }
  }, [queryKirtanId]);

  // Auto scroll to top when queryKirtanId changes
  // useEffect(() => {
  //   if (kirtanId) {
  //     window.scrollTo({ top: 0, behavior: "smooth" });
  //   }
  // }, [kirtanId]);

  const handleOnFilterCancleClick = (data: selectedFilterDataType) => {
    // Add safety checks for data and text
    if (!data || !data.text) {
      return;
    }

    const lowerText = data?.text?.toLowerCase() || "";

    const updatedFilterParams = new URLSearchParams(searchParams);

    const filterKeys = ["bhav", "raagdhal", "rachayta", "k_book", "k_place", "k_prasang"];
    let isRemoving = false;

    for (const key of filterKeys) {
      const values = updatedFilterParams.get(key)?.split(",").filter(Boolean) || [];
      const updatedValues = values.filter((val) => val?.toLowerCase() !== lowerText);

      if (updatedValues.length < values.length) {
        isRemoving = true;
      }

      if (updatedValues.length > 0) {
        updatedFilterParams.set(key, updatedValues.join(","));
      } else {
        updatedFilterParams.delete(key);
      }
    }

    if (isRemoving && queryKirtanId) {
      updatedFilterParams.delete("kirtanId");
      setSelectedKirtanDetail(null);
      setQueryKirtanId(null);
    }

    const filterString = updatedFilterParams.toString();
    navigate(`/kirtan-lyrics${filterString ? `?${filterString}` : ""}`);
  };

  async function fetchKirtanLyricsData() {
    await IndexedDBService.initDB();

    const syncData = (await IndexedDBService.getData(1, storeNames.kirtanSyncDate)) as { id: number; syncDate: string } | undefined;
    const syncDate = syncData ? dayjs(syncData.syncDate) : null;

    if (syncDate === null || (syncDate && syncDate.isBefore(dayjs(), "day"))) {
      const response = (await apiService.getKirtanLyricsSyncData(syncDate?.format("YYYY-MM-DD") || null)) as KirtanLyricsSyncData;
      const createdData = response?.created ?? [];
      const updatedData = response?.updated ?? [];
      const deletedData = response?.deleted ?? [];

      // Apply delta to IndexedDB (all three independently, not else-if)
      if (createdData.length > 0) {
        await Promise.all(
          createdData.map((item) => {
            if (!item.i) return Promise.reject(new Error("Item missing 'i' field"));
            return IndexedDBService.addItem(item, storeNames.kirtanLyrics);
          }),
        );
      }
      if (updatedData.length > 0) {
        await Promise.all(
          updatedData.map((item) => {
            if (!item.i) return Promise.reject(new Error("Item missing 'i' field"));
            return IndexedDBService.updateItem(item, storeNames.kirtanLyrics);
          }),
        );
      }
      if (deletedData.length > 0) {
        await Promise.all(
          deletedData.map((item) => {
            if (!item.i) return Promise.reject(new Error("Item missing 'i' field"));
            return IndexedDBService.deleteItem(item.i, storeNames.kirtanLyrics);
          }),
        );
      }

      await IndexedDBService.updateItem({ id: 1, syncDate: dayjs().format("YYYY-MM-DD") }, storeNames.kirtanSyncDate);
    }

    // Always read the full dataset from IndexedDB after any sync branch.
    // Previously setAllData was called with just the delta — when the delta was
    // empty (nothing changed since last sync) allData became [] → "no data found".
    const allDbData = await IndexedDBService.getAllData(storeNames.kirtanLyrics);
    setAllData(allDbData as KirtanLyricsdata[]);
  }

  // Clear global search text when leaving kirtan-lyrics on mobile
  useEffect(() => {
    return () => {
      if (isMobile) dispatch(setSearchText(""));
    };
  }, [isMobile, dispatch]);

  // On mobile, when user clears the search while a kirtan detail is open, close the detail panel
  const prevReduxSearchText = useRef<string | null | undefined>("");
  useEffect(() => {
    if (isMobile && prevReduxSearchText.current && !reduxSearchText && queryKirtanId) {
      setSelectedKirtanDetail(null);
      setQueryKirtanId(null);
      if (typeof window !== "undefined") {
        const url = new URL(window.location.href);
        url.searchParams.delete("kirtanId");
        window.history.replaceState(null, "", url.toString());
        window.dispatchEvent(new CustomEvent("kirtanIdChanged", { detail: { kirtanId: null } }));
      }
    }
    prevReduxSearchText.current = reduxSearchText;
  }, [reduxSearchText, isMobile, queryKirtanId]);

  // Fetch only on mount / language change.
  // selectedFilters is intentionally excluded: filtering is done client-side
  // via filteredData (useMemo). Including it caused fetchKirtanLyricsData to
  // re-run on every filter change, briefly emptying allData mid-render.
  useEffect(() => {
    fetchKirtanLyricsData();
    dispatch(setHoveredChar(null));
    setPadDataCache(new Map());
    return () => {
      IndexedDBService.closeDB();
    };
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update Redux when filtered data changes
  useEffect(() => {
    dispatch(setKirtanLyricsData(filteredData));
  }, [filteredData, dispatch]);

  return (
    <div className="w-full mt-3 flex justify-center items-start gap-4 h-auto animate-in fade-in duration-300">
      <div className="w-full md:body_container flex flex-col lg:flex-row lg:gap-5 justify-start items-start relative">
        <div className="w-full flex gap-1 lg:gap-3 relative">
          <div
            className={cn(
              "w-[20%] md:w-[25%] hidden lg:flex lg:flex-col sticky top-20 overflow-y-auto scrollbar-hide",
              lang === LANG.gujarati ? "kirtan_container_gujarati" : "kirtan_container",
            )}
          >
            <div className="relative w-full  bg-primary  border-borderPrimary">
              <input
                type="text"
                placeholder={lang === LANG.gujarati ? "કીર્તન ગીત શોધો..." : "Search kirtan lyrics..."}
                value={searchText}
                onChange={(e) => setSearchText_local(e.target.value)}
                className={cn(
                  "w-full px-4 py-2 rounded-full bg-white border-[1px] border-borderPrimary text-textHeadingColor placeholder-textHeadingColor body_text_s outline-none  transition-all duration-200",
                  normalInter,
                )}
              />
              {searchText && (
                <button
                  onClick={() => setSearchText_local("")}
                  className="absolute right-5 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-secondaryBackground transition-all duration-200"
                  title={lang === LANG.gujarati ? "સાફ કરો" : "Clear"}
                >
                  <IoClose size={18} className="text-textColor" />
                </button>
              )}
            </div>
            <KirtanLyricsFilter
              onFilterApplied={() => setQueryKirtanId(null)}
              bhav={BhavData}
              raagDhaal={RaagDhaalData}
              rachayta={CreatorData}
              k_book={KirtanBookData}
              k_place={KirtanPlaceData}
              k_prasang={KirtanPrasangData}
              selectedBhav={selectedFilters.bhav}
              selectedRaagDhal={selectedFilters.raag_dhal}
              selectedRachayta={selectedFilters.rachayta}
              selectedKBook={selectedFilters.k_book}
              selectedKPlace={selectedFilters.k_place}
              selectedKPrasang={selectedFilters.k_prasang}
            />
          </div>

          <div
            className={cn(
              "pl-3 bg-secondaryBackground rounded-[8px] overflow-hidden flex justify-start items-start ",
              // lang === LANG.gujarati ? "kirtan_container_gujarati" : "kirtan_container",
              queryKirtanId ? "hidden lg:block lg:w-[50%] 2xl:w-[60%]" : " w-full lg:w-full",
            )}
          >
            <KirtanLyricsData
              kirtanId={kirtanId}
              selectedKirtanDetail={selectedKirtanDetail}
              selectedKirtanId={queryKirtanId ? Number(queryKirtanId) : null}
              kirtanLyricsData={filteredData}
              rechaytaData={CreatorData}
            />
          </div>

          <div
            className={cn(
              "sticky top-20 w-auto",
              lang === LANG.gujarati ? "kirtan_container_gujarati" : "kirtan_container",
              "flex z-10",
              queryKirtanId ? "hidden lg:flex" : "flex",
            )}
          >
            <KirtanCharactersScrollBar kirtanCharacters={kirtanCharacters} />
          </div>

          <AnimatePresence mode="popLayout">
            {queryKirtanId && (
              <motion.div
                key="detail-panel"
                initial={{ opacity: 0, x: isMobile ? 2 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: isMobile ? 2 : 50 }}
                transition={{ duration: 0.1, ease: "easeInOut" }}
                layout
                className={cn(
                  "flex sticky top-20 w-full lg:min-w-[10rem] lg:w-[30rem] 2xl:w-[70rem] container-size flex-col bg-primary rounded-lg border border-borderPrimary",
                  lang === LANG.gujarati
                    ? "md:h-[calc(100dvh-150px)] 2xl:h-[calc(100dvh-150px)]"
                    : "md:h-[calc(100dvh-150px)] 2xl:h-[calc(100dvh-150px)]",
                )}
              >
                <KirtanDetailPanel
                  kirtanPadListData={
                    (queryKirtanId && padDataCache.has(Number(queryKirtanId)) ? padDataCache.get(Number(queryKirtanId)) : selectedPadList) ||
                    kirtanPadListData ||
                    []
                  }
                  gayakData={GayakData}
                  kirtanId={queryKirtanId ? Number(queryKirtanId) : null}
                  onClose={handleCloseDetailPanel}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {!queryKirtanId && (
          <div className="w-full h-auto flex flex-col gap-4 lg:hidden px-3 justify-start items-center relative">
            <Drawer>
              <DrawerTrigger asChild>
                {!isTouchBar && (
                  <div
                    className={cn(
                      "fixed  right-7 z-20 size-10 rounded-full bg-darkOrangeColor flex justify-center items-center shadow-lg transition-all duration-300 ease-out",
                      showTopButton ? "bottom-20" : "bottom-10",
                    )}
                  >
                    {allSelectedFilterData.length > 0 ? (
                      <FunnelX size={18} fill="white" className="text-white" />
                    ) : (
                      <Funnel size={18} fill="white" className="text-white" />
                    )}
                  </div>
                )}
              </DrawerTrigger>
              <DrawerContent className="!p-0 md:!pb-6 w-full max-md:h-[90vh] overflow-hidden flex flex-col !rounded-t-2xl" showHandle={false}>
                {/* <div className="md:overflow-y-auto md:px-4 flex-1 h-full"> */}
                <KirtanLyricsFilter
                  onFilterApplied={() => setQueryKirtanId(null)}
                  bhav={BhavData}
                  raagDhaal={RaagDhaalData}
                  rachayta={CreatorData}
                  k_book={KirtanBookData}
                  k_place={KirtanPlaceData}
                  k_prasang={KirtanPrasangData}
                  selectedBhav={selectedFilters.bhav}
                  selectedRaagDhal={selectedFilters.raag_dhal}
                  selectedRachayta={selectedFilters.rachayta}
                  selectedKBook={selectedFilters.k_book}
                  selectedKPlace={selectedFilters.k_place}
                  selectedKPrasang={selectedFilters.k_prasang}
                />
                {/* </div> */}
              </DrawerContent>
            </Drawer>
            {allSelectedFilterData.length > 0 && (
              <div className="w-full flex justify-start items-start overflow-x-auto gap-2 mb-1.5 pb-1.5">
                {allSelectedFilterData.map((filter) => {
                  return (
                    <div
                      className="flex w-auto p-1 justify-center items-center rounded-full gap-2 bg-primary border-[1px] border-borderPrimary"
                      key={filter.id}
                    >
                      <span className="text-12 text-textColor font-inter font-normal line-clamp-1">{filter.text}</span>
                      <button className="clickable-btn rounded-full p-1 bg-lightbackground" onClick={() => handleOnFilterCancleClick(filter)}>
                        <IoClose size={18} className="text-textColor" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export default KirtanLyrics;
